import React, { useState, useEffect } from 'react';
import { Button, Snackbar, Alert, Box, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showIOSInstall, setShowIOSInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    if (isIOS) {
      // Check if not already in standalone mode
      if (!(window.navigator as any).standalone) {
        setShowIOSInstall(true);
      }
      return;
    }

    // Listen for Chrome/Edge install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted PWA install');
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
  };

  // Don't show anything if already installed
  if (isInstalled) {
    return null;
  }

  // iOS Manual Install Instructions
  if (showIOSInstall) {
    return (
      <Snackbar 
        open={showIOSInstall} 
        onClose={() => setShowIOSInstall(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowIOSInstall(false)} 
          severity="info" 
          sx={{ width: '100%' }}
          icon={<PhoneIphoneIcon />}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Install SOG App
          </Typography>
          <Typography variant="caption" display="block">
            Tap <strong>Share</strong> button → <strong>Add to Home Screen</strong>
          </Typography>
        </Alert>
      </Snackbar>
    );
  }

  // Chrome/Edge Install Button
  if (deferredPrompt) {
    return (
      <Box sx={{ 
        position: 'fixed', 
        bottom: 80, 
        right: 20, 
        zIndex: 1000 
      }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<GetAppIcon />}
          onClick={handleInstallClick}
          sx={{
            borderRadius: '24px',
            textTransform: 'none',
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6
            }
          }}
        >
          Install App
        </Button>
      </Box>
    );
  }

  return null;
};

export default PWAInstallPrompt;